import { __decorate } from "tslib";
import throttle from 'just-throttle';
import { property, state } from 'lit/decorators.js';
import { consumeContext, watchContext } from '../../base/context';
import { eventListener, isPointerEvent } from '../../base/events';
import { mediaContext, MediaRemoteControl } from '../../media';
import { setAttribute } from '../../utils/dom';
import { clampNumber, round } from '../../utils/number';
import { formatSpokenTime } from '../../utils/time';
import { SliderElement } from '../slider';
export const TIME_SLIDER_ELEMENT_TAG_NAME = 'vds-time-slider';
/**
 * A slider that lets the user control the current media playback time.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `media-can-play`: Applied when media can begin playback.
 *
 * @tagname vds-time-slider
 * @example
 * ```html
 * <vds-time-slider label="Media time slider"></vds-time-slider>
 * ```
 * @example
 * ```css
 * vds-time-slider {
 *   --vds-slider-track-height: 2.5px;
 *   --vds-slider-thumb-width: 16px;
 *   --vds-slider-thumb-height: 16px;
 *   --vds-slider-active-color: #ff2a5d;
 * }
 * ```
 */
export class TimeSliderElement extends SliderElement {
    constructor() {
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        super(...arguments);
        this.label = 'Media time slider';
        this.shiftKeyMultiplier = 2;
        /**
         * Represents the current % of media playback.
         *
         * @internal
         */
        this.value = -1;
        // These properties are overriden in final render by methods below.
        /** @internal */
        this.min = 0;
        /** @internal */
        this.max = 100;
        /** @internal */
        this.valueMin = '0';
        /** @internal */
        this.valueNow = '0';
        /** @internal */
        this.valueMax = '0';
        this._step = 0.25;
        this._keyboardStep = 5;
        /**
         * ♿ **ARIA:** Human-readable text alternative for the current slider value. If you pass
         * in a string containing `{currentTime}` or `{duration}` templates they'll be replaced with
         * the spoken form such as `1 hour 30 minutes`.
         */
        this.valueText = '{currentTime} out of {duration}';
        /**
         * Whether the scrubber should request playback to pause while the user is dragging the
         * thumb. If the media was playing before the dragging starts, the state will be restored by
         * dispatching a user play request once the dragging ends.
         */
        this.pauseWhileDragging = false;
        /**
         * The amount of milliseconds to throttle media seeking request events being dispatched.
         */
        this.seekingRequestThrottle = 100;
        this._mediaCurrentTime = 0;
        this._mediaDuration = 0;
        this._mediaPaused = mediaContext.paused.initialValue;
        this._mediaRemote = new MediaRemoteControl(this);
        this._dispatchSeekingRequest = throttle((event) => {
            this._mediaRemote.seeking(this.currentTime, event);
        }, this.seekingRequestThrottle);
        this._wasPlayingBeforeDragStart = false;
    }
    /**
     *  A number that specifies the granularity that the slider value must adhere to in seconds.
     * For example, a step with the value `1` indicates a granularity of 1 second increments.
     *
     * @default 0.25
     */
    get step() {
        return this._mediaDuration > 0
            ? round((this._step / this._mediaDuration) * 100, 2)
            : this._step;
    }
    set step(newStep) {
        this._step = newStep;
    }
    /**
     * ♿ **ARIA:** A number that specifies the number of seconds to step when interacting
     * with the slider via keyboard.
     *
     * @default 5
     */
    get keyboardStep() {
        return this._mediaDuration > 0
            ? round((this._keyboardStep / this._mediaDuration) * 100, 2)
            : this._keyboardStep;
    }
    set keyboardStep(newStep) {
        this._keyboardStep = newStep;
    }
    /**
     * The current media time.
     */
    get currentTime() {
        return this._mediaDuration * (this.value / 100);
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    update(changedProperties) {
        if (changedProperties.has('disabled') && this.disabled) {
            this._dispatchSeekingRequest.cancel();
        }
        super.update(changedProperties);
    }
    disconnectedCallback() {
        this._dispatchSeekingRequest.cancel();
        super.disconnectedCallback();
    }
    // -------------------------------------------------------------------------------------------
    // Methods
    // -------------------------------------------------------------------------------------------
    _getValueNow() {
        const valueNow = this._mediaDuration * (this.value / 100);
        return String(Math.round(valueNow));
    }
    _getValueMax() {
        return String(Math.round(this._mediaDuration));
    }
    _getValueText() {
        const currentTime = this._mediaDuration * (this.value / 100);
        return this.valueText
            .replace('{currentTime}', formatSpokenTime(currentTime))
            .replace('{duration}', formatSpokenTime(this._mediaDuration));
    }
    _handleSliderDragStart(event) {
        this._togglePlaybackWhileDragging(event);
    }
    _handleSliderValueChange(event) {
        this.value = event.detail;
        if (this.isDragging) {
            this._dispatchSeekingRequest(event);
        }
        if (!isPointerEvent(event.originalEvent)) {
            this._dispatchSeekingRequest.cancel();
            this._mediaRemote.seek(this.currentTime, event);
        }
    }
    _handleSliderDragEnd(event) {
        this._dispatchSeekingRequest.cancel();
        this._mediaRemote.seek(this.currentTime, event);
        this._togglePlaybackWhileDragging(event);
    }
    _updateValueToCurrentTime() {
        if (this.isDragging)
            return;
        const percentage = this._mediaDuration > 0
            ? (this._mediaCurrentTime / this._mediaDuration) * 100
            : 0;
        this.value = clampNumber(0, round(percentage, 5), 100);
    }
    _handleCanPlayContextUpdate(canPlay) {
        setAttribute(this, 'media-can-play', canPlay);
    }
    _togglePlaybackWhileDragging(event) {
        if (!this.pauseWhileDragging)
            return;
        if (this.isDragging && !this._mediaPaused) {
            this._wasPlayingBeforeDragStart = true;
            this._mediaRemote.pause(event);
        }
        else if (this._wasPlayingBeforeDragStart &&
            !this.isDragging &&
            this._mediaPaused) {
            this._wasPlayingBeforeDragStart = false;
            this._mediaRemote.play(event);
        }
    }
}
__decorate([
    property({ attribute: false, state: true })
], TimeSliderElement.prototype, "value", void 0);
__decorate([
    property({ attribute: false })
], TimeSliderElement.prototype, "min", void 0);
__decorate([
    property({ attribute: false })
], TimeSliderElement.prototype, "max", void 0);
__decorate([
    property({ attribute: false })
], TimeSliderElement.prototype, "valueMin", void 0);
__decorate([
    property({ attribute: false })
], TimeSliderElement.prototype, "valueNow", void 0);
__decorate([
    property({ attribute: false })
], TimeSliderElement.prototype, "valueMax", void 0);
__decorate([
    property({ type: Number })
], TimeSliderElement.prototype, "step", null);
__decorate([
    property({ attribute: 'keyboard-step', type: Number })
], TimeSliderElement.prototype, "keyboardStep", null);
__decorate([
    property({ attribute: 'pause-while-dragging', type: Boolean })
], TimeSliderElement.prototype, "pauseWhileDragging", void 0);
__decorate([
    property({ attribute: 'seeking-request-throttle', type: Number })
], TimeSliderElement.prototype, "seekingRequestThrottle", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.currentTime)
], TimeSliderElement.prototype, "_mediaCurrentTime", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.duration, { transform: (d) => (d >= 0 ? d : 0) })
], TimeSliderElement.prototype, "_mediaDuration", void 0);
__decorate([
    state(),
    consumeContext(mediaContext.paused)
], TimeSliderElement.prototype, "_mediaPaused", void 0);
__decorate([
    eventListener('vds-slider-drag-start')
], TimeSliderElement.prototype, "_handleSliderDragStart", null);
__decorate([
    eventListener('vds-slider-value-change')
], TimeSliderElement.prototype, "_handleSliderValueChange", null);
__decorate([
    eventListener('vds-slider-drag-end')
], TimeSliderElement.prototype, "_handleSliderDragEnd", null);
__decorate([
    watchContext(mediaContext.currentTime),
    watchContext(mediaContext.duration)
], TimeSliderElement.prototype, "_updateValueToCurrentTime", null);
__decorate([
    watchContext(mediaContext.canPlay)
], TimeSliderElement.prototype, "_handleCanPlayContextUpdate", null);
